/**
 * One question, open for editing.
 *
 * The card is draggable by its handle and drops onto another card's position inside a step; the
 * arrows next to it do the same thing without a mouse, because a builder that can only be
 * reordered by dragging is a builder half the people cannot reorder.
 *
 * The `key` field follows the base-language label until someone types in it, and then stops for
 * good. Once the survey has an answer it is disabled outright: it is the CSV column name and the
 * thread between versions, and renaming it splits the question in two — 214 answers under a key
 * that "disappeared" and a new one starting at zero, with no error anywhere (R5).
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTTextArea, UTTextField } from '@group-link-one/gl-ui/utilities';
import type { DragEvent, JSX } from 'react';
import { MatrixRowsEditor } from './MatrixRowsEditor';
import { QuestionConfigPanel } from './QuestionConfigPanel';
import { QuestionOptionsEditor } from './QuestionOptionsEditor';
import type {
  MatrixRowType,
  SurveyLanguage,
  SurveyQuestionType,
} from '../../../Types/Surveys.types';
import { SURVEY_QUESTION_TYPES } from '../../../Types/Surveys.types';
import type {
  QuestionConfigNumbers,
  QuestionConfigTexts,
  SurveyQuestionDraft,
} from '../../../Utils/surveyForm';
import { BASE_LANGUAGE } from '../../../Utils/surveyForm';
import { QUESTION_TYPE_HINTS, QUESTION_TYPE_LABELS } from '../../../Utils/surveyFormat';
import { IconButton } from '@/Shared/components/IconButton/IconButton';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
`;

const draggingStyle = css`
  opacity: 0.45;
`;

const dropTargetStyle = css`
  border-top: 2px solid var(--color-primary);
`;

const headStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const handleStyle = css`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const positionStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const selectStyle = css`
  height: 36px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-xs);
`;

const flagStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
  cursor: pointer;
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--space-s) var(--space-m);
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/** The JSON key, in mono, so the fields and the document read as the same thing. */
const monoLabelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const fieldNameStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const hintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const typeHintStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

export interface QuestionEditorProps {
  question: SurveyQuestionDraft;
  index: number;
  total: number;
  step: number;
  language: SurveyLanguage;
  /** True once the survey has answers and this question already existed. */
  frozen: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  onDragStart: (uid: string) => void;
  onDragEnd: () => void;
  onDragOver: (uid: string) => void;
  onDrop: (uid: string) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onTypeChange: (type: SurveyQuestionType) => void;
  onKeyChange: (value: string) => void;
  onLabelChange: (value: string) => void;
  onHelpTextChange: (value: string) => void;
  onRequiredChange: (value: boolean) => void;
  onNumberChange: (field: keyof QuestionConfigNumbers, value: string) => void;
  onConfigTextChange: (field: keyof QuestionConfigTexts, value: string) => void;
  onOptionValueChange: (optionUid: string, value: string) => void;
  onOptionLabelChange: (optionUid: string, value: string) => void;
  onOptionFlagChange: (
    optionUid: string,
    flag: 'isExclusive' | 'allowsFreeText',
    value: boolean,
  ) => void;
  onOptionMove: (optionUid: string, direction: -1 | 1) => void;
  onOptionAdd: () => void;
  onOptionRemove: (optionUid: string) => void;
  onRowKeyChange: (rowUid: string, value: string) => void;
  onRowLabelChange: (rowUid: string, value: string) => void;
  onRowTypeChange: (rowUid: string, type: MatrixRowType) => void;
  onRowRequiredChange: (rowUid: string, value: boolean) => void;
  onRowAdd: () => void;
  onRowRemove: (rowUid: string) => void;
}

export const QuestionEditor = ({
  question,
  index,
  total,
  step,
  language,
  frozen,
  isDragging,
  isDropTarget,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onMove,
  onRemove,
  onDuplicate,
  onTypeChange,
  onKeyChange,
  onLabelChange,
  onHelpTextChange,
  onRequiredChange,
  onNumberChange,
  onConfigTextChange,
  onOptionValueChange,
  onOptionLabelChange,
  onOptionFlagChange,
  onOptionMove,
  onOptionAdd,
  onOptionRemove,
  onRowKeyChange,
  onRowLabelChange,
  onRowTypeChange,
  onRowRequiredChange,
  onRowAdd,
  onRowRemove,
}: QuestionEditorProps): JSX.Element => {
  const id = `survey-builder-question-${question.uid}`;
  const carriesOptions =
    question.type === 'SINGLE_CHOICE' ||
    question.type === 'MULTI_CHOICE' ||
    question.type === 'MATRIX';

  const handleDragOver = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    onDragOver(question.uid);
  };

  const handleDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    onDrop(question.uid);
  };

  return (
    <article
      id={id}
      className={`${cardStyle} ${isDragging ? draggingStyle : ''} ${isDropTarget ? dropTargetStyle : ''}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={headStyle}>
        {/* The handle carries the drag, not the card: making the whole card draggable would
            start a drag every time someone selected text inside one of its fields. */}
        <button
          type='button'
          className={handleStyle}
          draggable
          aria-label={`Arrastar a pergunta ${index + 1} do passo ${step}`}
          onDragStart={() => {
            onDragStart(question.uid);
          }}
          onDragEnd={onDragEnd}
        >
          <GLIcon variant='Settings.DotsSixVertical' size={16} fill='var(--color-text-muted)' />
        </button>
        <span className={positionStyle}>
          {step}.{index + 1}
        </span>

        <select
          id={`${id}-type`}
          className={selectStyle}
          value={question.type}
          aria-label='Tipo da pergunta'
          onChange={event => {
            onTypeChange(event.target.value as SurveyQuestionType);
          }}
        >
          {SURVEY_QUESTION_TYPES.map(type => (
            <option key={type} value={type}>
              {QUESTION_TYPE_LABELS[type]}
            </option>
          ))}
        </select>

        <label className={flagStyle} htmlFor={`${id}-required`}>
          <input
            id={`${id}-required`}
            type='checkbox'
            checked={question.isRequired}
            onChange={event => {
              onRequiredChange(event.target.checked);
            }}
          />
          obrigatória
        </label>

        <div className={spacerStyle} />

        <div className={actionsStyle}>
          <IconButton
            id={`${id}-up`}
            icon='Arrows.ArrowUp'
            size='sm'
            aria-label={`Subir a pergunta ${index + 1}`}
            disabled={index === 0}
            onClick={() => {
              onMove(-1);
            }}
          />
          <IconButton
            id={`${id}-down`}
            icon='Arrows.ArrowDown'
            size='sm'
            aria-label={`Descer a pergunta ${index + 1}`}
            disabled={index === total - 1}
            onClick={() => {
              onMove(1);
            }}
          />
          <IconButton
            id={`${id}-duplicate`}
            icon='Communication.CopySimple'
            size='sm'
            aria-label={`Duplicar a pergunta ${index + 1}`}
            onClick={onDuplicate}
          />
          <IconButton
            id={`${id}-remove`}
            icon='Misc.Trash'
            size='sm'
            variant='danger'
            aria-label={`Remover a pergunta ${index + 1}`}
            onClick={onRemove}
          />
        </div>
      </div>

      <p className={typeHintStyle}>{QUESTION_TYPE_HINTS[question.type]}</p>

      <div className={gridStyle}>
        <div className={fieldStyle} style={{ gridColumn: 'span 4' }}>
          <span className={monoLabelStyle}>
            key <span className={fieldNameStyle}>· identificador</span>
          </span>
          <UTTextField
            id={`${id}-key`}
            placeholder='nota_geral'
            value={question.key}
            disabled={frozen || language !== BASE_LANGUAGE}
            onChange={event => {
              onKeyChange(event.target.value);
            }}
          />
          <span className={hintStyle}>
            {frozen
              ? 'Travado: a pesquisa já tem resposta, e renomear partiria a contagem em duas.'
              : question.keyTouched
                ? 'Editado à mão — não segue mais o enunciado.'
                : 'Preenchido a partir do enunciado em inglês, até você digitar aqui.'}
          </span>
        </div>

        <div className={fieldStyle} style={{ gridColumn: 'span 8' }}>
          <span className={monoLabelStyle}>
            label <span className={fieldNameStyle}>· enunciado</span>
          </span>
          <UTTextField
            id={`${id}-label`}
            placeholder='O que você quer perguntar?'
            value={question.labels[language]}
            onChange={event => {
              onLabelChange(event.target.value);
            }}
          />
        </div>

        <div className={fieldStyle} style={{ gridColumn: '1 / -1' }}>
          <span className={monoLabelStyle}>
            helpText <span className={fieldNameStyle}>· texto de apoio (opcional)</span>
          </span>
          <UTTextArea
            id={`${id}-help-text`}
            textAreaSize='small'
            value={question.helpTexts[language]}
            onChange={event => {
              onHelpTextChange(event.target.value);
            }}
          />
        </div>
      </div>

      <QuestionConfigPanel
        id={id}
        type={question.type}
        numbers={question.numbers}
        texts={question.configTexts[language]}
        language={language}
        onNumberChange={onNumberChange}
        onTextChange={onConfigTextChange}
      />

      {carriesOptions ? (
        <QuestionOptionsEditor
          id={id}
          isScale={question.type === 'MATRIX'}
          options={question.options}
          language={language}
          frozen={frozen}
          onValueChange={onOptionValueChange}
          onLabelChange={onOptionLabelChange}
          onFlagChange={onOptionFlagChange}
          onMove={onOptionMove}
          onAdd={onOptionAdd}
          onRemove={onOptionRemove}
        />
      ) : null}

      {question.type === 'MATRIX' ? (
        <MatrixRowsEditor
          id={id}
          rows={question.rows}
          language={language}
          frozen={frozen}
          onKeyChange={onRowKeyChange}
          onLabelChange={onRowLabelChange}
          onTypeChange={onRowTypeChange}
          onRequiredChange={onRowRequiredChange}
          onAdd={onRowAdd}
          onRemove={onRowRemove}
        />
      ) : null}
    </article>
  );
};
